import React, { useEffect, useState, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { WidgetContainer } from '@/components/dashboard/widget-container';
import { widgetRegistry, registerWidgets } from '@/lib/dashboard/registry';
import { useAuthStore } from '@/lib/stores/auth';
import { useAdminStore } from '@/lib/stores/admin';
import { PageGuard } from '@/components/auth/page-guard';
import { LayoutGrid, List, RefreshCw } from 'lucide-react';
import { WidgetCategory, WidgetSize } from '@/lib/dashboard/types';
import { ROLE_NAMES, type RoleName } from '@/lib/constants/roles';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { api } from '@/lib/api/client';
import { WelcomeModal, ONBOARDING_STORAGE_KEY } from '@/components/onboarding/welcome-modal';
import { PageHeader } from '@/components/page/page-header';
import { PageToolbar } from '@/components/page/page-toolbar';
import { AsyncErrorState } from '@/components/ui/async-error-state';

// Import drawer components
import { AddUserDrawer } from '@/components/dashboard/add-user-drawer';
import { BulkUserImportDrawer } from '@/components/dashboard/bulk-user-import-drawer';

// Widget components
// import { SystemHealthWidget } from '@/components/dashboard/widgets/system-health-widget';
import { QuickActionsWidget } from '@/components/dashboard/widgets/quick-actions-widget';
import { StatsCard, StatsGrid, UserStatsCard, StreamStatsCard, CommentStatsCard } from '@/components/dashboard/widgets/stats-card';
import { LatestStreamsWidget } from '@/components/dashboard/latest-streams-widget';
import {
  Activity,
  Users,
  Play,
  MessageCircle,
  Eye,
  AlertTriangle,
  Video,
  FileText,
  TrendingUp
} from 'lucide-react';

// Dashboard stats response type
interface DashboardStatsResponse {
  role: string;
  stats: {
    users?: { total: number; active: number; newToday: number };
    streams: { live: number; total?: number; accessible?: number };
    comments?: { pending: number; total: number };
    qa?: { total?: number; answered: number; pending: number; responseTime?: string; asked?: number };
    recordings?: { total: number };
    viewers?: { total: number };
    moderated?: number;
    activity?: { today: number };
  };
}

/**
 * Register all available dashboard widgets
 * This function should be called once when the dashboard loads
 */
function registerAllWidgets() {
  const widgets = [
    // FIRST WIDGET - Latest Streams (most important for users)
    {
      id: 'latest-streams',
      title: 'Latest Streams',
      description: 'Recent streams available to you',
      component: LatestStreamsDashboardWidget,
      permissions: [],
      roles: [ROLE_NAMES.PASTOR, ROLE_NAMES.ADMIN, ROLE_NAMES.MEDIA_TEAM, ROLE_NAMES.MODERATOR, ROLE_NAMES.MEMBER] as RoleName[],
      size: 'large' as WidgetSize,
      category: 'user' as WidgetCategory,
      icon: <TrendingUp className="h-5 w-5" />
    },

    // Core widgets available to all roles
    {
      id: 'quick-actions',
      title: 'Quick Actions',
      description: 'Common actions for your role',
      component: QuickActionsWidget,
      permissions: [],
      roles: [ROLE_NAMES.ADMIN, ROLE_NAMES.MEDIA_TEAM, ROLE_NAMES.MODERATOR, ROLE_NAMES.PASTOR, ROLE_NAMES.MEMBER] as RoleName[],
      size: 'medium' as WidgetSize,
      category: 'core' as WidgetCategory,
      icon: <Activity className="h-5 w-5" />
    },

    // Admin-only widgets
    {
      id: 'user-management',
      title: 'User Management',
      description: 'Manage user accounts and permissions',
      component: UserManagementWidget,
      permissions: ['manage_users'],
      roles: [ROLE_NAMES.ADMIN] as RoleName[],
      size: 'large' as WidgetSize,
      category: 'admin' as WidgetCategory,
      icon: <Users className="h-5 w-5" />
    },
    {
      id: 'user-activity-timeline',
      title: 'User Activity Timeline',
      description: 'Recent user activities and system events',
      component: UserActivityTimelineWidget,
      permissions: ['view_audit_logs'],
      roles: [ROLE_NAMES.ADMIN] as RoleName[],
      size: 'medium' as WidgetSize,
      category: 'admin' as WidgetCategory,
      icon: <Activity className="h-5 w-5" />
    },
    {
      id: 'stream-management',
      title: 'Stream Management',
      description: 'Create and manage live streams',
      component: StreamManagementWidget,
      permissions: ['manage_streams'],
      roles: [ROLE_NAMES.ADMIN, ROLE_NAMES.MEDIA_TEAM] as RoleName[],
      size: 'large' as WidgetSize,
      category: 'media' as WidgetCategory,
      icon: <Video className="h-5 w-5" />
    },
    {
      id: 'comment-moderation',
      title: 'Comment Moderation',
      description: 'Moderate user comments and interactions',
      component: CommentModerationWidget,
      permissions: ['moderate_comments'],
      roles: [ROLE_NAMES.ADMIN, ROLE_NAMES.MODERATOR] as RoleName[],
      size: 'large' as WidgetSize,
      category: 'moderation' as WidgetCategory,
      icon: <MessageCircle className="h-5 w-5" />
    },

    // Media team widgets
    {
      id: 'recording-management',
      title: 'Recording Management',
      description: 'Manage stream recordings and uploads',
      component: RecordingManagementWidget,
      permissions: ['manage_recordings'],
      roles: [ROLE_NAMES.ADMIN, ROLE_NAMES.MEDIA_TEAM] as RoleName[],
      size: 'medium' as WidgetSize,
      category: 'media' as WidgetCategory,
      icon: <FileText className="h-5 w-5" />
    },

    // Moderator widgets
    {
      id: 'live-monitoring',
      title: 'Live Stream Monitoring',
      description: 'Monitor active streams for moderation',
      component: LiveMonitoringWidget,
      permissions: ['monitor_streams'],
      roles: [ROLE_NAMES.MODERATOR] as RoleName[],
      size: 'medium' as WidgetSize,
      category: 'moderation' as WidgetCategory,
      icon: <Eye className="h-5 w-5" />
    },
    {
      id: 'moderation-activity',
      title: 'Moderation Activity',
      description: 'Recent moderation actions and statistics',
      component: ModerationActivityWidget,
      permissions: ['moderate_comments'],
      roles: [ROLE_NAMES.MODERATOR] as RoleName[],
      size: 'medium' as WidgetSize,
      category: 'moderation' as WidgetCategory,
      icon: <Activity className="h-5 w-5" />
    },

    // User widgets
    {
      id: 'my-streams',
      title: 'My Streams',
      description: 'Streams you have access to',
      component: MyStreamsWidget,
      permissions: [],
      roles: [ROLE_NAMES.PASTOR, ROLE_NAMES.ADMIN, ROLE_NAMES.MEDIA_TEAM, ROLE_NAMES.MODERATOR, ROLE_NAMES.MEMBER] as RoleName[],
      size: 'medium' as WidgetSize,
      category: 'user' as WidgetCategory,
      icon: <Play className="h-5 w-5" />
    }
  ];

  registerWidgets(widgets);
}

export default function DashboardPage() {
  const { user, isInitialized, isAuthenticated, isLoading } = useAuthStore();
  const navigate = useNavigate();

  // 🔑 Role state (single source of truth for this page)
  const [role, setRole] = useState<RoleName | null>(null);

  // Onboarding welcome modal
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);
  const [onboardingLoading, setOnboardingLoading] = useState(true);

  // Safe localStorage helper — guards against Safari private mode / QuotaExceededError
  const lsGet = (key: string) => { try { return localStorage.getItem(key); } catch { return null; } };
  const lsSet = (key: string, value: string) => { try { localStorage.setItem(key, value); } catch { /* ignore */ } };

  // Check onboarding status from backend once user is loaded
  useEffect(() => {
    if (!isInitialized || !isAuthenticated || !user) {
      setOnboardingLoading(true);
      return;
    }

    const checkOnboardingStatus = async () => {
      // If user already dismissed locally, skip API call entirely
      if (lsGet(ONBOARDING_STORAGE_KEY)) {
        setOnboardingLoading(false);
        return;
      }

      try {
        const response = await api.get<{ onboardingCompleted: boolean }>('/api/settings/onboarding/status');
        if (response.success) {
          const isCompleted = response.data?.onboardingCompleted ?? false;
          if (!isCompleted) {
            setShowWelcomeModal(true);
          } else {
            // Backend says complete — persist to localStorage so we skip next time
            lsSet(ONBOARDING_STORAGE_KEY, 'true');
          }
        }
      } catch (error) {
        console.error('Failed to check onboarding status:', error);
        // Fallback: show modal if localStorage not set (already checked above)
        setShowWelcomeModal(true);
      } finally {
        setOnboardingLoading(false);
      }
    };

    checkOnboardingStatus();
  }, [isInitialized, isAuthenticated, user]);

  const handleWelcomeClose = async () => {
    try {
      // Mark onboarding as complete on backend
      const response = await api.post('/api/settings/onboarding/complete', {});
      if (response.success) {
        // Also set localStorage as cache
        lsSet(ONBOARDING_STORAGE_KEY, 'true');
        setShowWelcomeModal(false);
      }
    } catch (error) {
      console.error('Failed to mark onboarding as complete:', error);
      // Fallback: still hide the modal and set localStorage
      lsSet(ONBOARDING_STORAGE_KEY, 'true');
      setShowWelcomeModal(false);
    }
  };

  // Fetch admin stats if user is admin and auth is ready
  useEffect(() => {
    if (isInitialized && isAuthenticated && user?.role?.name === 'admin') {
      useAdminStore.getState().fetchStats().catch((error) => {
        console.error('[Dashboard] Failed to fetch admin stats:', error);
      });
    }
  }, [isInitialized, isAuthenticated, user?.role?.name]);

  // Sync role when auth is ready
  useEffect(() => {
    if (!isInitialized || !isAuthenticated || !user?.role?.name) {
      setRole(null);
      console.log('[Dashboard] Role sync - not ready yet:', { isInitialized, isAuthenticated, userRole: user?.role?.name });
      return;
    }

    const userRole = user.role.name as RoleName;
    setRole(userRole);
  }, [isInitialized, isAuthenticated, user?.role?.name, user?.email]);

  // Filter and sort widgets
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [filters, setFilters] = useState<Record<string, string | string[]>>({});
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);

  // Get filter values from the filters object
  const categoryFilter = (filters.category as string) || 'all';
  const sizeFilter = (filters.size as string) || 'all';
  const sortBy = (filters.sortBy as string) || 'default';

  // Drawer state management
  const [isAddUserDrawerOpen, setIsAddUserDrawerOpen] = useState(false);
  const [isBulkImportDrawerOpen, setIsBulkImportDrawerOpen] = useState(false);

  const availableWidgets = useMemo(() => {
    if (!user) {
      return [];
    }
    return widgetRegistry.getWidgetsForUser(user);
  }, [user]);

  // Get unique categories from available widgets
  const availableCategories = useMemo(() => {
    const categories = new Set(availableWidgets.map(w => w.category));
    return Array.from(categories).sort();
  }, [availableWidgets]);

  // Filter and sort widgets
  const filteredWidgets = useMemo(() => {
    let filtered = availableWidgets;

    // Apply category filter
    if (categoryFilter !== 'all') {
      filtered = filtered.filter((w: any) => w.category === categoryFilter);
    }

    // Apply size filter
    if (sizeFilter !== 'all') {
      filtered = filtered.filter((w: any) => w.size === sizeFilter);
    }

    // Sort widgets
    filtered.sort((a: any, b: any) => {
      switch (sortBy) {
        case 'name':
          return a.title.localeCompare(b.title);
        case 'category':
          return a.category.localeCompare(b.category);
        default:
          return 0;
      }
    });

    return filtered;
  }, [availableWidgets, categoryFilter, sizeFilter, sortBy]);

  const handleRefresh = () => {
    // Force re-render of all widgets
    window.location.reload();
  };

  const handleWidgetAction = (action: string, data?: any) => {
    console.log('Widget action:', action, data);

    // Handle common actions
    switch (action) {
      case 'view_streams':
        navigate('/streams');
        break;
      case 'add_user':
        navigate('/admin/users');
        break;
      case 'view_analytics':
        navigate('/admin');
        break;
      case 'moderate_comments':
        navigate('/moderator');
        break;
      case 'join_stream':
        navigate('/streams');
        break;
      case 'view_all_streams':
        navigate('/streams');
        break;
      case 'my_comments':
        navigate('/comments');
        break;
      case 'system_settings':
        navigate('/settings');
        break;
      case 'start_stream':
        navigate('/admin/streams/new');
        break;
      case 'upload_recording':
        navigate('/admin/recordings');
        break;
      case 'monitor_streams':
        navigate('/moderator');
        break;
      case 'view_reports':
        navigate('/moderator');
        break;
      case 'moderation_queue':
        navigate('/moderator');
        break;
      case 'view_live_monitoring':
        navigate('/streams?status=live');
        break;
      case 'bulk_user_import':
        setIsBulkImportDrawerOpen(true);
        break;
      case 'view_user_management':
        navigate('/admin/users');
        break;
      case 'view_stream_management':
        navigate('/admin/streams');
        break;
      case 'view_comment_moderation':
        navigate('/moderator');
        break;
      case 'view_system_analytics':
        navigate('/admin');
        break;
      case 'view_recording_management':
        navigate('/admin/recordings');
        break;
      case 'view_my_streams':
        navigate('/streams');
        break;
      case 'view_full_audit_logs':
        navigate('/admin');
        break;
      case 'view_moderation_history':
        navigate('/moderator');
        break;
      default:
        console.log('Unhandled action:', action);
    }
  };

  const gridClasses = viewMode === 'grid'
    ? 'grid grid-cols-1 gap-6'
    : 'space-y-4';

  // Show loading state while auth is initializing
  if (!isInitialized || isLoading) {
    return (
      <PageGuard>
        <div className="dashboard-layout space-y-6">
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
              <p className="text-muted-foreground">Loading dashboard...</p>
            </div>
          </div>
        </div>
      </PageGuard>
    );
  }

  return (
    <PageGuard>
      {/* First-login welcome modal */}
      <WelcomeModal open={showWelcomeModal} onClose={handleWelcomeClose} />

      <div className="@container/dashboard max-w-[1800px] w-full mx-auto px-3 py-4 @[740px]/dashboard:px-4 @[740px]/dashboard:py-5 @[1100px]/dashboard:px-6 @[1100px]/dashboard:py-6 space-y-4 @[740px]/dashboard:space-y-5 @[1100px]/dashboard:space-y-6">
        <PageHeader
          title="Dashboard"
        />

        <div className="@container/page-toolbar">
          <PageToolbar
            trailing={(
              <>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={handleRefresh}
                  aria-label="Refresh dashboard"
                  title="Refresh"
                >
                  <RefreshCw className="h-4 w-4" />
                </Button>

                <Select value={viewMode} onValueChange={(value: 'grid' | 'list') => setViewMode(value)}>
                  <SelectTrigger className="h-9 w-[160px]" aria-label="Select view mode">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="grid">
                      <div className="flex items-center gap-2">
                        <LayoutGrid className="h-4 w-4" />
                        Grid
                      </div>
                    </SelectItem>
                    <SelectItem value="list">
                      <div className="flex items-center gap-2">
                        <List className="h-4 w-4" />
                        List
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </>
            )}
          />
        </div>

        {/* Widgets */}
        {availableWidgets.length > 0 && (
          filteredWidgets.length === 0 ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <LayoutGrid className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2">No widgets available</h3>
                <p className="text-muted-foreground">
                  {availableWidgets.length === 0
                    ? "No widgets are configured for your role."
                    : "Try adjusting your filters to see more widgets."
                  }
                </p>
              </div>
            </div>
          ) : (
            <div className={gridClasses}>
              {filteredWidgets.map((widget: any) => (
                <WidgetContainer
                  key={widget.id}
                  widgetId={widget.id}
                  role={role}
                  size={widget.size}
                  showHeader={false}
                  onAction={handleWidgetAction}
                />
              ))}
            </div>
          )
        )}

        {/* Drawer Components */}
        <AddUserDrawer
          isOpen={isAddUserDrawerOpen}
          onClose={() => setIsAddUserDrawerOpen(false)}
          onSuccess={(user) => {
            toast.success(`User ${(user as any)?.name || (user as any)?.email || ''} created successfully`);
            setIsAddUserDrawerOpen(false);
          }}
        />

        <BulkUserImportDrawer
          isOpen={isBulkImportDrawerOpen}
          onClose={() => setIsBulkImportDrawerOpen(false)}
          onSuccess={(result) => {
            const r = result as any;
            const count = r?.imported ?? r?.count ?? r?.users?.length ?? 0;
            toast.success(`${count} user${count !== 1 ? 's' : ''} imported successfully`);
            setIsBulkImportDrawerOpen(false);
          }}
        />
      </div>
    </PageGuard>
  );
}

/**
 * Dashboard widget components
 */

// Import the dashboard components
import { RecentUsersList } from '@/components/admin/recent-users-list';
import { RecentActivitiesList } from '@/components/admin/recent-activities-list';
import { RecentStreamsList } from '@/components/admin/recent-streams-list';
import { PendingCommentsList } from '@/components/admin/pending-comments-list';
import { RecentRecordingsList } from '@/components/admin/recent-recordings-list';
import { UserStreamsList } from '@/components/admin/user-streams-list';
import { LiveMonitoringWidget as LiveMonitoringWidgetComponent } from '@/components/admin/live-monitoring-widget';

function UserManagementWidget({ user, onAction }: any) {
  return (
    <RecentUsersList
      maxUsers={5}
      onViewMore={() => onAction?.('view_user_management')}
    />
  );
}

function StreamManagementWidget({ user, onAction }: any) {
  return (
    <RecentStreamsList
      maxStreams={5}
      onViewMore={() => onAction?.('view_stream_management')}
    />
  );
}

function CommentModerationWidget({ user, onAction }: any) {
  return (
    <PendingCommentsList
      maxComments={5}
      onViewMore={() => onAction?.('view_comment_moderation')}
    />
  );
}


function RecordingManagementWidget({ user, onAction }: any) {
  return (
    <RecentRecordingsList
      maxRecordings={5}
      onViewMore={() => onAction?.('view_recording_management')}
    />
  );
}

function LiveMonitoringWidget({ user, onAction }: any) {
  return (
    <LiveMonitoringWidgetComponent
      maxStreams={5}
      refreshInterval={30000}
      onViewMore={() => onAction?.('view_live_monitoring')}
    />
  );
}

function LatestStreamsDashboardWidget({ user, onAction }: any) {
  return (
    <LatestStreamsWidget
      maxStreams={5}
      onViewMore={() => onAction?.('view_all_streams')}
    />
  );
}

function MyStreamsWidget({ user, onAction }: any) {
  return (
    <UserStreamsList
      maxStreams={5}
      onViewMore={() => onAction?.('view_my_streams')}
    />
  );
}


// Dashboard stats widget implementations
function OverviewStatsWidget({ role, onAction }: any) {
  const [stats, setStats] = React.useState<DashboardStatsResponse['stats'] | null>(null);
  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const { isReady } = { isReady: true }; // useAuthReady();

  const fetchStats = React.useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await api.get<DashboardStatsResponse>('/api/dashboard/stats');

      if (response.success && response.data) {
        setStats(response.data.stats);
      } else {
        setError('Failed to load dashboard statistics');
      }
    } catch (err) {
      console.error('Error fetching dashboard stats:', err);
      setError('Failed to load dashboard statistics');
    } finally {
      setIsLoading(false);
    }
  }, []);

  React.useEffect(() => {
    if (!isReady) {
      console.log('[OverviewStatsWidget] Waiting for auth to be ready...');
      return;
    }
    fetchStats();
  }, [role, isReady, fetchStats]);

  if (isLoading || !isReady) {
    return (
      <div className="space-y-4">
        <StatsGrid minCardWidth={200}>
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="animate-pulse">
              <div className="h-20 bg-muted rounded-lg"></div>
            </div>
          ))}
        </StatsGrid>
      </div>
    );
  }

  if (error || !stats) {
    return (
      <AsyncErrorState
        title="Unable to load dashboard statistics"
        message={error ?? 'Failed to load dashboard statistics'}
        onRetry={() => void fetchStats()}
        disabled={isLoading}
      />
    );
  }

  const renderStatsBasedOnRole = () => {
    switch (role || 'member') {
      case 'admin':
        return (
          <StatsGrid minCardWidth={200}>
            <UserStatsCard
              count={stats.users?.total || 0}
              activeCount={stats.users?.active || 0}
              newToday={stats.users?.newToday || 0}
            />
            <StreamStatsCard
              liveCount={stats.streams.live}
              totalCount={stats.streams.total ?? 0}
            />
            <CommentStatsCard
              pendingCount={stats.comments?.pending || 0}
              totalCount={stats.comments?.total || 0}
            />
          </StatsGrid>
        );

      case 'moderator':
        return (
          <StatsGrid minCardWidth={200}>
            <StatsCard
              title="Pending Moderation"
              value={stats.comments?.pending || 0}
              description="Items awaiting review"
              icon={AlertTriangle}
              variant={(stats.comments?.pending || 0) > 0 ? 'warning' : 'default'}
            />
            <StatsCard
              title="Comments Moderated"
              value={stats.moderated || 0}
              description="This session"
              icon={MessageCircle}
              variant="success"
            />
            <StatsCard
              title="Active Streams"
              value={stats.streams.live}
              description="Currently monitored"
              icon={Eye}
              variant={stats.streams.live > 0 ? 'success' : 'default'}
            />
            <StatsCard
              title="Activity Today"
              value={stats.activity?.today || 0}
              description="Moderation actions"
              icon={Activity}
            />
          </StatsGrid>
        );

      case 'media_team':
        return (
          <StatsGrid minCardWidth={200}>
            <StreamStatsCard
              liveCount={stats.streams.live}
              totalCount={stats.streams.total ?? 0}
            />
            <StatsCard
              title="Total Recordings"
              value={stats.recordings?.total || 0}
              description="Available for download"
              icon={FileText}
            />
            <StatsCard
              title="Total Viewers"
              value={stats.viewers?.total || 0}
              description="All time"
              icon={Eye}
            />
          </StatsGrid>
        );

      case 'pastor':
      case 'member':
      default:
        return (
          <StatsGrid minCardWidth={200}>
            <StatsCard
              title="Accessible Streams"
              value={stats.streams.accessible ?? 0}
              description="Streams you can join"
              icon={Play}
            />
            <StatsCard
              title="Your Comments"
              value={stats.comments?.total ?? 0}
              description="Total comments made"
              icon={MessageCircle}
            />
          </StatsGrid>
        );
    }
  };

  return (
    <div className="space-y-4">
      {renderStatsBasedOnRole()}
    </div>
  );
}

const UserActivityTimelineWidget = React.memo(function UserActivityTimelineWidget({ role, onAction }: any) {
  // Only render if user has admin role
  if (role !== 'admin') {
    return null;
  }
  return (
    <RecentActivitiesList
      maxActivities={5}
      onViewMore={() => onAction?.('view_full_audit_logs')}
    />
  );
});

function getModerationActionFromLog(log: { action?: string }): string {
  const actionMap: Record<string, string> = {
    comment_approve: 'Approved comment',
    comment_reject: 'Rejected comment',
    comment_delete: 'Deleted comment',
    user_mute: 'Muted user',
    user_ban: 'Banned user',
  };
  return actionMap[log.action ?? ''] || log.action || 'Moderated content';
}

function getModerationTargetFromLog(log: {
  details?: { targetUser?: string; reason?: string; streamTitle?: string; commentId?: number };
  resourceId?: number;
  resourceType?: string;
  targetId?: number;
}): string {
  if (log.details?.targetUser) {
    return `${log.details.targetUser} ${log.details.reason || ''}`;
  }
  if (log.details?.streamTitle) {
    return `on "${log.details.streamTitle}"`;
  }
  if (log.details?.commentId != null) {
    return `Comment #${log.details.commentId}`;
  }
  if (log.resourceId != null && log.resourceType) {
    return `${String(log.resourceType)} #${log.resourceId}`;
  }
  if (log.targetId != null) {
    return `Target #${log.targetId}`;
  }
  return 'Activity (details unavailable)';
}

function getModerationTypeFromLog(log: { action?: string }): string {
  const typeMap: Record<string, string> = {
    comment_approve: 'approve',
    comment_reject: 'reject',
    comment_delete: 'delete',
    user_mute: 'restrict',
    user_ban: 'ban',
  };
  return typeMap[log.action ?? ''] || 'moderate';
}

function formatModerationLogTimeAgo(timestamp: string): string {
  const now = new Date();
  const time = new Date(timestamp);
  const diffMs = now.getTime() - time.getTime();
  const diffMins = Math.floor(diffMs / (1000 * 60));

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins} minutes ago`;

  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours} hours ago`;

  const diffDays = Math.floor(diffHours / 24);
  return `${diffDays} days ago`;
}

const ModerationActivityWidget = React.memo(function ModerationActivityWidget({ user, onAction }: any) {
  const [recentActivities, setRecentActivities] = React.useState<Array<{
    action: string;
    target: string;
    time: string;
    type: string;
  }>>([]);
  const [loading, setLoading] = React.useState(true);
  const [loadError, setLoadError] = React.useState<string | null>(null);
  const { isReady } = { isReady: true };

  const fetchModerationActivity = React.useCallback(async () => {
    try {
      setLoading(true);
      setLoadError(null);

      const response = await api.get<{ logs: any[] }>('/api/admin/audit-logs?limit=10&actionType=moderation');

      if (response.success && response.data && (response.data as any).logs) {
        const activities = (response.data as any).logs.map((log: any) => ({
          action: getModerationActionFromLog(log),
          target: getModerationTargetFromLog(log),
          time: formatModerationLogTimeAgo(log.timestamp || log.created_at),
          type: getModerationTypeFromLog(log)
        }));
        setRecentActivities(activities);
      } else {
        setRecentActivities([]);
      }
    } catch (error) {
      console.error('Failed to fetch moderation activity:', error);
      setLoadError('Failed to load recent activity');
      setRecentActivities([]);
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    if (!isReady) return;
    void fetchModerationActivity();
  }, [isReady, fetchModerationActivity]);

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold">Recent Activity</h3>
        </div>
        <div className="space-y-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="flex items-start space-x-3 p-3 rounded-lg border animate-pulse">
              <div className="w-2 h-2 bg-muted rounded-full mt-2"></div>
              <div className="flex-1 space-y-2">
                <div className="h-4 bg-muted rounded w-3/4"></div>
                <div className="h-3 bg-muted rounded w-1/2"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold">Recent Activity</h3>
          <button
            type="button"
            onClick={() => onAction?.('view_moderation_history')}
            className="text-sm text-primary hover:text-primary/80 underline"
          >
            View All →
          </button>
        </div>
        <AsyncErrorState
          message={loadError}
          onRetry={() => void fetchModerationActivity()}
          disabled={loading}
          compact
        />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">Recent Activity</h3>
        <button
          type="button"
          onClick={() => onAction?.('view_moderation_history')}
          className="text-sm text-primary hover:text-primary/80 underline"
        >
          View All →
        </button>
      </div>

      <div className="space-y-3">
        {recentActivities.map((activity, index) => (
          <div key={index} className="flex items-start space-x-3 p-3 rounded-lg border">
            <div className={`w-2 h-2 rounded-full mt-2 ${activity.type === 'approve' ? 'bg-green-500' :
              activity.type === 'hide' ? 'bg-yellow-500' :
                activity.type === 'delete' ? 'bg-red-500' :
                  'bg-blue-500'
              }`} />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium">{activity.action}</p>
              <p className="text-sm text-muted-foreground">{activity.target}</p>
              <p className="text-xs text-muted-foreground">{activity.time}</p>
            </div>
          </div>
        ))}
      </div>

      {recentActivities.length === 0 && (
        <div className="text-center py-8 text-muted-foreground">
          <Activity className="h-12 w-12 mx-auto mb-4 opacity-50" />
          <p>No recent moderation activity</p>
        </div>
      )}
    </div>
  );
});

// All const/React.memo components are defined above.
// Safe to call at module level now — registry is populated before first render.
registerAllWidgets();
